import React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Drawer,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Fullscreen, FullscreenExit, Close } from "@material-ui/icons";
import { JOINTS, AILMENTS, MARKETS, LOCALES, MEDICATION_SUBSTANCES } from "../../api";
import type { Joint, Ailment, Market, PreviewLocale, MedicationSubstance, PreviewAttrs } from "../../api";
import {
  AutoFillContext,
  FormValuesContext,
  FeelContext,
  ValidationContext,
  RespondedContext,
  type FormValues,
  type FormEntry,
} from "./ResolvedComponents";
import { previewStyles as styles } from "./previewStyles";

const formatAttrsSummary = (attrs: PreviewAttrs) => {
  const joint = attrs.ailment === "joint_pain" ? `${attrs.joint.replace(/_/g, " ")} · ` : "";
  const base = `${joint}${attrs.ailment.replace(/_/g, " ")} · ${attrs.market.toUpperCase()} · ${attrs.locale}`;
  return attrs.medication.length > 0 ? `${base} · ${attrs.medication.length} meds` : base;
};

const arraysEqual = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const hasResponse = (value: unknown): boolean =>
  value != null &&
  (typeof value === "string" ? value.trim().length > 0 : Array.isArray(value) ? value.length > 0 : true);

const attrsEqual = (a: PreviewAttrs, b: PreviewAttrs) =>
  a.ailment === b.ailment &&
  (a.ailment === "joint_pain" && b.ailment === "joint_pain" ? a.joint === b.joint : true) &&
  a.market === b.market &&
  a.locale === b.locale &&
  arraysEqual(a.medication, b.medication);

export const PatientPreviewShell: React.VFC<{
  isExpanded: boolean;
  onToggleExpand: () => void;
  isLoading: boolean;
  error: unknown;
  attrs: PreviewAttrs;
  onApplyAttrs: (attrs: PreviewAttrs) => void;
  seedFeelContext?: Record<string, unknown>;
  children: React.ReactNode;
}> = ({ isExpanded, onToggleExpand, isLoading, error, attrs, onApplyAttrs, seedFeelContext, children }) => {
  const [errorOpen, setErrorOpen] = React.useState(false);

  React.useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const errorMessage = error instanceof Error ? error.message : "Failed to load preview";

  const [attrsOpen, setAttrsOpen] = React.useState(false);
  const [draftAttrs, setDraftAttrs] = React.useState<PreviewAttrs>(attrs);
  const openAttrs = () => {
    setDraftAttrs(attrs);
    setAttrsOpen(true);
  };
  const closeAttrs = () => setAttrsOpen(false);
  const confirmAttrs = () => {
    onApplyAttrs(draftAttrs);
    setAttrsOpen(false);
  };
  const toggleMedication = (m: MedicationSubstance) =>
    setDraftAttrs(prev => {
      const selected = new Set(prev.medication);
      if (selected.has(m)) selected.delete(m);
      else selected.add(m);
      return { ...prev, medication: MEDICATION_SUBSTANCES.filter(s => selected.has(s)) };
    });

  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearKey, setClearKey] = React.useState(0);
  const [formValues, setFormValues] = React.useState<FormValues>({});
  const formValuesRef = React.useRef<FormValues>(formValues);
  formValuesRef.current = formValues;
  const requiredIdsRef = React.useRef<Map<number, number | null>>(new Map());
  const [errorIds, setErrorIds] = React.useState<Set<number>>(new Set());

  const formValuesApi = React.useMemo(
    () => ({
      register: (questionId: number, entry: FormEntry) =>
        setFormValues(prev => (prev[questionId] === entry ? prev : { ...prev, [questionId]: entry })),
      unregister: (questionId: number) =>
        setFormValues(prev => {
          if (!(questionId in prev)) return prev;
          const next = { ...prev };
          delete next[questionId];
          return next;
        }),
    }),
    []
  );

  // The FEEL context and the answered-question set are pure derivations of the
  // current answers plus the schema baseline (`seedFeelContext`: every option
  // feel-id defined). Deriving instead of accumulating means visibility never
  // depends on mount/registration order, and a predicate like `q = optionX`
  // can't collapse into `null = null` just because `q` is unanswered.
  const feelContext = React.useMemo(() => {
    const ctx: Record<string, unknown> = { ...seedFeelContext };
    for (const entry of Object.values(formValues)) {
      if (entry.feelId) ctx[entry.feelId] = entry.feelValue;
    }
    return ctx;
  }, [seedFeelContext, formValues]);

  const respondedIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const entry of Object.values(formValues)) {
      if (hasResponse(entry.value)) ids.add(entry.questionId);
    }
    return ids;
  }, [formValues]);

  React.useEffect(() => {
    setErrorIds(prev => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter(id => !hasResponse(formValues[id]?.value)));
      return next.size === prev.size ? prev : next;
    });
  }, [formValues]);

  const validationApi = React.useMemo(
    () => ({
      registerRequired: (id: number, pageIndex: number | null) => {
        requiredIdsRef.current.set(id, pageIndex);
      },
      unregisterRequired: (id: number) => {
        requiredIdsRef.current.delete(id);
      },
    }),
    []
  );

  const validate = React.useCallback((pageIndex?: number | null): boolean => {
    const errors = new Set<number>();
    for (const [id, page] of requiredIdsRef.current) {
      if (pageIndex != null && page !== pageIndex) continue;
      if (!hasResponse(formValuesRef.current[id]?.value)) {
        errors.add(id);
      }
    }
    setErrorIds(errors);
    return errors.size === 0;
  }, []);

  const validationContextValue = React.useMemo(
    () => ({ ...validationApi, errorIds, validate }),
    [validationApi, errorIds, validate]
  );

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = Object.values(formValuesRef.current).map(({ questionId, value }) => ({ questionId, value }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "health-form-values.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.previewContainer}>
      <Box display="flex" alignItems="center" px={1} pt={1} style={{ gap: 4 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={openAttrs}
          style={{ fontSize: 11, height: 24, textTransform: "none", padding: "0 8px" }}
        >
          {formatAttrsSummary(attrs)}
        </Button>
        {isLoading && <CircularProgress size={14} />}
      </Box>
      <Drawer anchor="right" open={attrsOpen} onClose={closeAttrs}>
        <Box display="flex" flexDirection="column" style={{ width: 420, height: "100%" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
            <Typography variant="h6">Preview attributes</Typography>
            <IconButton size="small" onClick={closeAttrs}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Box flex={1} px={2} py={2} display="flex" flexDirection="column" style={{ gap: 16, overflowY: "auto" }}>
            <Box display="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <TextField
                select
                variant="outlined"
                label="Joint"
                disabled={draftAttrs.ailment !== "joint_pain"}
                value={draftAttrs.ailment === "joint_pain" ? draftAttrs.joint : ""}
                onChange={e =>
                  setDraftAttrs(prev =>
                    prev.ailment === "joint_pain" ? { ...prev, joint: e.target.value as Joint } : prev
                  )
                }
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {JOINTS.map(j => (
                  <MenuItem key={j} value={j}>
                    {j.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label="Ailment"
                value={draftAttrs.ailment}
                onChange={e => {
                  const ailment = e.target.value as Ailment;
                  setDraftAttrs(prev =>
                    ailment === "joint_pain"
                      ? { ...prev, ailment, joint: prev.ailment === "joint_pain" ? prev.joint : "knee" }
                      : { ailment, market: prev.market, locale: prev.locale, medication: prev.medication }
                  );
                }}
              >
                {AILMENTS.map(a => (
                  <MenuItem key={a} value={a}>
                    {a.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label="Market"
                value={draftAttrs.market}
                onChange={e => setDraftAttrs({ ...draftAttrs, market: e.target.value as Market })}
              >
                {MARKETS.map(m => (
                  <MenuItem key={m} value={m}>
                    {m.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label="Locale"
                value={draftAttrs.locale}
                onChange={e => setDraftAttrs({ ...draftAttrs, locale: e.target.value as PreviewLocale })}
              >
                {LOCALES.map(l => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" color="textSecondary">
                  Medication{draftAttrs.medication.length > 0 ? ` (${draftAttrs.medication.length})` : ""}
                </Typography>
                {draftAttrs.medication.length > 0 && (
                  <Button size="small" onClick={() => setDraftAttrs({ ...draftAttrs, medication: [] })}>
                    Clear
                  </Button>
                )}
              </Box>
              <Box display="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {MEDICATION_SUBSTANCES.map(m => (
                  <FormControlLabel
                    key={m}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={draftAttrs.medication.includes(m)}
                        onChange={() => toggleMedication(m)}
                      />
                    }
                    label={<Typography variant="body2">{m.replace(/_/g, " ")}</Typography>}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="flex-end" px={2} py={1.5} style={{ gap: 8 }}>
            <Button onClick={closeAttrs}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={confirmAttrs} disabled={attrsEqual(draftAttrs, attrs)}>
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorOpen(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box display="flex" alignItems="center" px={1} pt={0.5} style={{ gap: 8 }}>
        <Button
          size="small"
          onClick={() => setAutoFillTrigger(n => n + 1)}
          style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}
        >
          Autofill
        </Button>
        <Button
          size="small"
          onClick={() => {
            setClearKey(n => n + 1);
            setAutoFillTrigger(0);
            setFormValues({});
          }}
          style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}
        >
          Clear
        </Button>
        <Button size="small" onClick={handleSubmit} style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}>
          Submit
        </Button>
        <Box style={{ flex: 1 }} />
        <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
          <IconButton size="small" onClick={onToggleExpand}>
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && !isLoading && (
        <Box px={2} py={4} textAlign="center">
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}
      <RespondedContext.Provider value={respondedIds}>
        <FormValuesContext.Provider value={formValuesApi}>
          <FeelContext.Provider value={feelContext}>
            <ValidationContext.Provider value={validationContextValue}>
              <AutoFillContext.Provider value={autoFillTrigger} key={clearKey}>
                {children}
              </AutoFillContext.Provider>
            </ValidationContext.Provider>
          </FeelContext.Provider>
        </FormValuesContext.Provider>
      </RespondedContext.Provider>
    </div>
  );
};
